import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// MUI Icons
import EditIcon from '@mui/icons-material/Edit';
import AddCommentIcon from '@mui/icons-material/AddComment';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

// Custom Components
import DraggableDialog from '@/ui/Dialog/DraggableDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import useFormController from '@/components/useFormController';
import GridContainer from '@/ui/Grid/GridContainer';
import FormContent from '@/components/FormContent';

import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import UserContext from '@/context/UserContext/UserContext';
import DataController from '@/lib/DataController';
import modelComments from '@/models/comments';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';
import DialogActionButton from '@/ui/Dialog/DialogActionButton';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import Permissions from '@/lib/permissions';
import CommentAdministrationDialog from './CommentAdministrationDialog';

const TenantCommentDialog = (props: IDialogProps) => {
  const { mode, recordId, onClose, form, initialRecord, level, baseRecordPath } = props;

  const { t } = useTranslation();
  const { hideDialog, showDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const [adminChanged, setAdminChanged] = useState(false);

  const dc = new DataController(modelComments, baseRecordPath);

  const close = (result: any) => {
    if (onClose) {
      onClose(result);
    }

    if (level) {
      hideDialog(level);
    } else {
      hideDialog();
    }
  };

  const {
    record,
    validation,
    dataChanged,
    fields,
    onFieldChange,
    doInsert,
    doUpdate,
    doDelete,
  } = useFormController({
    mode,
    recordId,
    form,
    dc,
    onClose: close,
    initialRecord,
  });

  const handleFieldChange = (value: any, source: string) => {
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleClose = () => {
    if (adminChanged) {
      close({ dataChanged: true, action: 'update' });
      return;
    }

    close({ success: false });
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.comment_insert');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert' });
          } else if (result.error) {
            const msg = t(result.error);
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = t('messages.comment_update');
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else if (result.error) {
            const msg = result.error;
            showNotification(msg, 'error');
          } else if (result.validationPass === false) {
            // do nothing
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleDelete = () => {
    doDelete().then((result) => {
      if (result.success) {
        showNotification(t('messages.deleteRecord'), 'success');
        close({ dataChanged: true, action: 'delete' });
      }
    }).catch((resp) => {
      if (resp.error) {
        const msg = t('error.delete') + resp.error;
        showNotification(msg, 'error');
      }
    });
  };

  const handleAdministrationClose = (result: any) => {
    if (result?.dataChanged) {
      setAdminChanged(true);
    }
  };

  const handleAdministrationOpen = () => {
    showDialog(
      CommentAdministrationDialog,
      {
        initialRecord: {
          id: record.id,
          assigned_to_id: record.assigned_to_id ?? null,
          status_id: record.status_id ?? 10,
        },
        onClose: handleAdministrationClose,
      },
      2
    );
  };

  const showAdministration =
    mode === 'update' &&
    !!record.id &&
    !!userContext?.hasAnyPermission([Permissions.TenantCommentAdministration]);

  return (
    <DraggableDialog open maxWidth="sm" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          {mode === 'update' ? (
            <EditIcon sx={{ transform: 'scale(1.3)' }} />
          ) : mode === 'insert' ? (
            <AddCommentIcon sx={{ transform: 'scale(1.3)' }} />
          ) : null}
          <DialogToolbarHeading>
            {mode === 'insert'
              ? t('titles.add_comment')
              : mode === 'update'
                ? t('titles.edit_comment')
                : null}
          </DialogToolbarHeading>
          <ToolbarFillContent />
          {mode === 'update' && (
            <DialogToolbarMenuButton>
              <DialogToolbarMenuItemDelete onClick={handleDelete} />
            </DialogToolbarMenuButton>
          )}
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer direction="row" spacing={2} my={2}>
          <FormContent
            fieldNames={dc.getFormFieldsNames(form)}
            record={record}
            validation={validation}
            fields={fields}
            onFieldChange={handleFieldChange}
            mode={mode}
            columns={1}
          />
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} />
        {showAdministration ? (
          <DialogActionButton
            variant="outlined"
            onClick={handleAdministrationOpen}
            startIcon={<ManageAccountsIcon />}
            ariaControls="administration"
          >
            {t('common.administration')}
          </DialogActionButton>
        ) : null}
        {mode === 'insert'
          ? <DialogActionButtonSave variant="contained" onClick={handleInsert} />
          : mode === 'update'
            ? <DialogActionButtonSave variant="contained" onClick={handleUpdate} disabled={!dataChanged} />
            : null}
      </DialogActions>
    </DraggableDialog>
  );
};

export default TenantCommentDialog;
